import logger from '../../utils/logger';

// DataForSEO Google Maps/Local Finder Live Advanced API integration for Competitors

const DATAFORSEO_BASE_URL = 'https://api.dataforseo.com/v3/business_data/google/maps/live/advanced';

// Helper function to calculate distance between two coordinates in kilometers
function calculateDistance(lat1, lng1, lat2, lng2) {
  const R = 6371; // Earth's radius in kilometers
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLng = (lng2 - lng1) * Math.PI / 180;
  const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
    Math.sin(dLng / 2) * Math.sin(dLng / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
}

export class GooglePlacesService {
  constructor(apiKey, useNewAPI = false, customSearchApiKey, searchEngineId) {
    // For DataForSEO, we expect apiKey to be in format "login:password"
    if (apiKey && apiKey.includes(':')) {
      const [login, password] = apiKey.split(':');
      this.login = login;
      this.password = password;
    } else {
      // Fallback - you might want to set these as environment variables
      this.login = apiKey || process.env.REACT_APP_DATAFORSEO_LOGIN;
      this.password = process.env.REACT_APP_DATAFORSEO_PASSWORD;
    }
    
    this.useNewAPI = useNewAPI;
    this.primaryCategoryFilter = null;
    this.baseUrl = DATAFORSEO_BASE_URL;
    
    logger.log('🔧 DataForSEO Service initialized for competitors module');
  }

  // Create authorization header for DataForSEO API
  getAuthHeader() {
    const credentials = btoa(`${this.login}:${this.password}`);
    return `Basic ${credentials}`;
  }

  async validateApiKey() {
    try {
      const response = await fetch(this.baseUrl, {
        method: 'POST',
        headers: {
          'Authorization': this.getAuthHeader(),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify([{
          keyword: 'coffee',
          location_coordinate: '40.7128,-74.0060',
          language_name: 'English',
          depth: 1
        }])
      });

      if (!response.ok) {
        logger.error('DataForSEO API validation failed:', response.status);
        return false;
      }

      const data = await response.json();
      return data && data.tasks && data.tasks.length > 0;
    } catch (error) {
      logger.error('DataForSEO API validation error:', error);
      return false;
    }
  }

  // Helper method to make requests with error handling
  async makeRequest(requestBody) {
    try {
      const response = await fetch(this.baseUrl, {
        method: 'POST',
        headers: {
          'Authorization': this.getAuthHeader(),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify([requestBody])
      });

      if (!response.ok) {
        throw new Error(`DataForSEO API request failed: ${response.status}`);
      }

      const data = await response.json();
      
      if (!data.tasks || data.tasks.length === 0) {
        throw new Error('No tasks returned from DataForSEO API');
      }

      const task = data.tasks[0];
      if (task.status_code !== 20000) {
        throw new Error(`DataForSEO API error: ${task.status_message}`);
      }

      return task.result || [];
    } catch (error) {
      logger.error('DataForSEO API request error:', error);
      throw error;
    }
  }

  // Convert DataForSEO result to Google Places format for compatibility
  convertToGooglePlacesFormat(dataForSEOResults) {
    if (!Array.isArray(dataForSEOResults)) {
      return [];
    }

    return dataForSEOResults.map(item => {
      const business = item.items ? item.items[0] : item;
      
      if (!business) return null;

      // DataForSEO field mapping
      const rating = business.rating?.value || business.rating || null;
      const reviewCount = business.rating?.votes_count || business.reviews_count || business.rating_count || 0;
      const photoCount = business.total_photos || (business.photos?.length) || 0;
      const website = business.url || business.website || null;
      const workHours = business.work_hours?.timetable || business.work_hours || business.hours || null;
      const categoryIds = business.category_ids || [];
      const primaryCategory = business.category || (business.categories && business.categories[0]) || null;

      return {
        place_id: business.place_id || `dataforseo_${business.cid || Math.random().toString(36)}`,
        placeId: business.place_id || `dataforseo_${business.cid || Math.random().toString(36)}`,
        name: business.title || business.name || 'Unknown Business',
        formatted_address: business.address || business.full_address || '',
        geometry: {
          location: {
            lat: business.latitude || (business.location ? business.location.latitude : 0),
            lng: business.longitude || (business.location ? business.location.longitude : 0)
          }
        },
        // Rating fields - DataForSEO uses rating.value
        rating: rating ? parseFloat(rating) : null,
        user_ratings_total: reviewCount,
        userRatingCount: reviewCount,
        reviewCount: reviewCount,
        // Photo fields - DataForSEO uses total_photos
        photos: business.photos || [],
        photoCount: photoCount,
        total_photos: photoCount, // Keep DataForSEO field for scoring
        // Category fields - DataForSEO uses category and category_ids
        price_level: business.price_level || null,
        types: categoryIds.length > 0 ? categoryIds : (primaryCategory ? [primaryCategory] : []),
        categories: business.categories || (primaryCategory ? [primaryCategory] : []),
        category_ids: categoryIds, // Keep DataForSEO field for scoring
        primaryType: primaryCategory,
        category: primaryCategory, // Keep DataForSEO field for scoring
        // Hours fields - DataForSEO uses work_hours.timetable
        business_status: 'OPERATIONAL',
        opening_hours: workHours,
        openingHours: workHours ? [workHours] : [],
        work_hours: business.work_hours, // Keep DataForSEO field for scoring
        // Contact fields - DataForSEO uses phone and url
        phone: business.phone || null,
        internationalPhoneNumber: business.phone || null,
        website: website,
        websiteUri: website,
        url: website, // Keep DataForSEO field for scoring
        // Additional fields
        cid: business.cid || null,
        is_claimed: business.is_claimed || false,
        book_online_url: business.book_online_url || null,
        dataForSEO: {
          original: business,
          source: 'dataforseo'
        }
      };
    }).filter(Boolean);
  }

  // Set primary category filter
  setPrimaryCategoryFilter(category) {
    this.primaryCategoryFilter = category;
    logger.log(`🏷️ DataForSEO Competitors: Primary category filter set to: ${category}`);
  }

  // Enhanced search method (main method used by competitors)
  async enhancedSearch(coordinates, radiusKm, query, businessName = null, address = null) {
    try {
      logger.log(`🚀 DataForSEO Competitors Enhanced Search: "${query}" at ${coordinates.lat},${coordinates.lng} (${radiusKm}km)`);

      const requestBody = {
        keyword: query,
        location_coordinate: `${coordinates.lat},${coordinates.lng}`,
        language_name: 'English',
        depth: 100,
        se_domain: 'google.com'
      };

      const results = await this.makeRequest(requestBody);
      let businesses = this.convertToGooglePlacesFormat(results);

      // Filter by distance
      if (radiusKm && radiusKm > 0) {
        businesses = businesses.filter(business => {
          const distance = calculateDistance(
            coordinates.lat,
            coordinates.lng,
            business.geometry.location.lat,
            business.geometry.location.lng
          );
          return distance <= radiusKm;
        });
      }

      // Apply primary category filter if set
      if (this.primaryCategoryFilter && businesses.length > 0) {
        const originalCount = businesses.length;
        businesses = this.filterByPrimaryCategory(businesses, this.primaryCategoryFilter);
        logger.log(`🚫 DataForSEO Competitors Filter: ${originalCount} → ${businesses.length} businesses (${this.primaryCategoryFilter})`);
      }

      // Sort by rating and review count
      businesses.sort((a, b) => {
        const scoreA = (a.rating || 0) * Math.log10((a.userRatingCount || 0) + 1);
        const scoreB = (b.rating || 0) * Math.log10((b.userRatingCount || 0) + 1);
        return scoreB - scoreA;
      });

      logger.log(`✅ DataForSEO Competitors: Found ${businesses.length} businesses for "${query}"`);
      return businesses;

    } catch (error) {
      logger.error(`❌ DataForSEO Competitors enhancedSearch failed:`, error);
      return [];
    }
  }

  // Text search method (for compatibility)
  async textSearch(coordinates, radiusKm, query, businessName = null, address = null) {
    return this.enhancedSearch(coordinates, radiusKm, query, businessName, address);
  }

  // Filter businesses by primary category (EXACT MATCH)
  filterByPrimaryCategory(businesses, targetCategory) {
    if (!targetCategory || !Array.isArray(businesses)) return businesses;

    const normalizedTarget = targetCategory.toLowerCase().trim();
    
    return businesses.filter(business => {
      // Check primary type first (EXACT MATCH)
      if (business.primaryType) {
        const normalizedPrimary = business.primaryType.toLowerCase().trim();
        if (normalizedPrimary === normalizedTarget) {
          return true;
        }
      }

      // Check categories/types array (EXACT MATCH)
      if (business.categories && Array.isArray(business.categories)) {
        return business.categories.some(category => {
          if (!category || typeof category !== 'string') return false;
          const normalizedCategory = category.toLowerCase().trim();
          return normalizedCategory === normalizedTarget;
        });
      }

      // Check types array (fallback - EXACT MATCH)
      if (business.types && Array.isArray(business.types)) {
        return business.types.some(type => {
          if (!type || typeof type !== 'string') return false;
          const normalizedType = type.toLowerCase().trim();
          return normalizedType === normalizedTarget;
        });
      }

      return false;
    });
  }

  // Get place details by place ID
  async getPlaceDetailsForEnrichment(placeId) {
    try {
      logger.log(`🔍 DataForSEO Competitors: Getting place details for ${placeId}`);
      
      if (placeId.startsWith('dataforseo_')) {
        logger.warn('DataForSEO place details lookup not fully implemented for generated IDs');
        return null;
      }

      return null;
    } catch (error) {
      logger.error('DataForSEO Competitors getPlaceDetailsForEnrichment error:', error);
      return null;
    }
  }

  // Test API connection
  async testApiConnection() {
    try {
      logger.log('🧪 Testing DataForSEO Competitors API connection...');
      const isValid = await this.validateApiKey();
      
      if (isValid) {
        logger.log('✅ DataForSEO Competitors API connection successful');
        return { success: true, message: 'DataForSEO API connection successful' };
      } else {
        logger.log('❌ DataForSEO Competitors API connection failed');
        return { success: false, message: 'DataForSEO API connection failed' };
      }
    } catch (error) {
      logger.error('❌ DataForSEO Competitors API test error:', error);
      return { success: false, message: `DataForSEO API test error: ${error.message}` };
    }
  }
}
